import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import CountUp from 'react-countup';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CalculatorContainer = styled.section`
  min-height: 100vh;
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  position: relative;
  overflow: hidden;
`;

const CalculatorContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.2rem;
  color: #94a3b8;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CalculatorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 3rem;
  margin-bottom: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const InputSection = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: fit-content;
`;

const ResultsSection = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #e2e8f0;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.1);
  }

  &::placeholder {
    color: #64748b;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.1);
  }

  option {
    background: #1e293b;
    color: #ffffff;
  }
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: none;
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
  }
`;

const CalculateButton = styled(motion.button)`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(59, 130, 246, 0.4);
  }
`;

const ResultCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ResultTitle = styled.h4`
  color: #3b82f6;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ResultValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const ResultDescription = styled.p`
  color: #94a3b8;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ChartContainer = styled.div`
  margin-top: 2rem;
  height: 300px;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ComparisonCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
`;

function InteractiveCalculator() {
  const [inputs, setInputs] = useState({
    connections: 1000,
    rebarGrade: 'Fe550',
    projectType: 'Commercial',
    laborRate: 300,
    couplerType: 'Threaded',
    projectScale: 'Medium'
  });

  const [results, setResults] = useState({
    materialSavings: 0,
    laborSavings: 0,
    totalSavings: 0,
    roi: 0,
    timeReduction: 0,
    couplerCost: 0
  });

  const [showResults, setShowResults] = useState(false);

  const calculateSavings = () => {
    const { connections, rebarGrade, projectType, laborRate, couplerType, projectScale } = inputs;
    
    // Material cost per kg based on grade
    const materialCosts = {
      'Fe500': 65,
      'Fe550': 68,
      'Fe600': 72
    };
    
    // Coupler costs based on type (range based on size)
    const couplerCosts = {
      'Threaded': 87,     // Average of 35-140 range
      'Swaged': 82,       // Average of 25-140 range  
      'Grout-filled': 100, // Average of 60-140 range
      'Adhesive': 97      // Average of 55-140 range
    };

    // Project type multipliers
    const projectMultipliers = {
      'Residential': 1.0,
      'Commercial': 1.15,
      'Infrastructure': 1.25,
      'Industrial': 1.20
    };

    // Scale multipliers
    const scaleMultipliers = {
      'Small': 0.8,
      'Medium': 1.0,
      'Large': 1.3,
      'Mega': 1.6
    };

    const materialCost = materialCosts[rebarGrade] || 65;
    const couplerCost = couplerCosts[couplerType] || 87;
    const projectMultiplier = projectMultipliers[projectType] || 1.0;
    const scaleMultiplier = scaleMultipliers[projectScale] || 1.0;

    // Calculate savings
    const materialSavings = Math.round(connections * materialCost * 0.18 * projectMultiplier * scaleMultiplier);
    const laborSavings = Math.round(connections * laborRate * 0.033 * scaleMultiplier);
    const totalCouplerCost = connections * couplerCost;
    const totalSavings = materialSavings + laborSavings;
    const roi = Math.round((totalSavings / totalCouplerCost) * 100);
    const timeReduction = Math.round(50 * scaleMultiplier); // Base 50% improvement

    setResults({
      materialSavings,
      laborSavings,
      totalSavings,
      roi,
      timeReduction,
      couplerCost: totalCouplerCost
    });

    setShowResults(true);
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Chart data for savings breakdown
  const savingsChartData = {
    labels: ['Material Savings', 'Labor Savings', 'Coupler Investment'],
    datasets: [{
      data: [results.materialSavings, results.laborSavings, results.couplerCost],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(6, 182, 212, 0.8)',
        'rgba(239, 68, 68, 0.8)'
      ],
      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(6, 182, 212, 1)',
        'rgba(239, 68, 68, 1)'
      ],
      borderWidth: 2
    }]
  };

  // Comparison data
  const comparisonData = {
    traditional: {
      cost: results.totalSavings + results.couplerCost,
      time: 100,
      quality: 75,
      reliability: 80
    },
    couplers: {
      cost: results.couplerCost,
      time: 100 - results.timeReduction,
      quality: 95,
      reliability: 99.8
    }
  };

  return (
    <CalculatorContainer id="calculator" data-section="calculator">
      <CalculatorContent>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <SectionTitle>Interactive Cost Calculator</SectionTitle>
          <SectionSubtitle>
            Calculate real-time savings for your specific project parameters
          </SectionSubtitle>
        </motion.div>

        <CalculatorGrid>
          <InputSection
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 style={{ marginBottom: '1.5rem', color: '#3b82f6' }}>Project Parameters</h3>
            
            <FormGroup>
              <Label>Number of Connections</Label>
              <Input
                type="number"
                value={inputs.connections}
                onChange={(e) => handleInputChange('connections', parseInt(e.target.value) || 0)}
                placeholder="Enter number of connections"
              />
              <Slider
                type="range"
                min="100"
                max="50000"
                value={inputs.connections}
                onChange={(e) => handleInputChange('connections', parseInt(e.target.value))}
              />
            </FormGroup>

            <FormGroup>
              <Label>Rebar Grade</Label>
              <Select
                value={inputs.rebarGrade}
                onChange={(e) => handleInputChange('rebarGrade', e.target.value)}
              >
                <option value="Fe500">Fe500 (₹65/kg)</option>
                <option value="Fe550">Fe550 (₹68/kg)</option>
                <option value="Fe600">Fe600 (₹72/kg)</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Project Type</Label>
              <Select
                value={inputs.projectType}
                onChange={(e) => handleInputChange('projectType', e.target.value)}
              >
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Infrastructure">Infrastructure</option>
                <option value="Industrial">Industrial</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Labor Rate (₹/hour)</Label>
              <Input
                type="number"
                value={inputs.laborRate}
                onChange={(e) => handleInputChange('laborRate', parseInt(e.target.value) || 0)}
                placeholder="Enter labor rate"
              />
            </FormGroup>

            <FormGroup>
              <Label>Coupler Type</Label>
              <Select
                value={inputs.couplerType}
                onChange={(e) => handleInputChange('couplerType', e.target.value)}
              >
                <option value="Threaded">Threaded (₹35-140/unit)</option>
                <option value="Swaged">Swaged (₹25-140/unit)</option>
                <option value="Grout-filled">Grout-filled (₹60-140/unit)</option>
                <option value="Adhesive">Adhesive (₹55-140/unit)</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Project Scale</Label>
              <Select
                value={inputs.projectScale}
                onChange={(e) => handleInputChange('projectScale', e.target.value)}
              >
                <option value="Small">Small (&lt; 500 connections)</option>
                <option value="Medium">Medium (500-5000)</option>
                <option value="Large">Large (5000-20000)</option>
                <option value="Mega">Mega (&gt; 20000)</option>
              </Select>
            </FormGroup>

            <CalculateButton
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={calculateSavings}
            >
              Calculate Savings
            </CalculateButton>
          </InputSection>

          <ResultsSection
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 style={{ marginBottom: '1.5rem', color: '#3b82f6' }}>Results & Analysis</h3>
            
            <AnimatePresence>
              {showResults && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <ResultCard>
                    <ResultTitle>Total Project Savings</ResultTitle>
                    <ResultValue>
                      ₹<CountUp end={results.totalSavings} duration={2} separator="," />
                    </ResultValue>
                    <ResultDescription>
                      Combined material and labor cost savings
                    </ResultDescription>
                  </ResultCard>

                  <ResultCard>
                    <ResultTitle>Return on Investment</ResultTitle>
                    <ResultValue>
                      <CountUp end={results.roi} duration={2} />%
                    </ResultValue>
                    <ResultDescription>
                      ROI based on coupler investment vs savings
                    </ResultDescription>
                  </ResultCard>

                  <ResultCard>
                    <ResultTitle>Time Reduction</ResultTitle>
                    <ResultValue>
                      <CountUp end={results.timeReduction} duration={2} />%
                    </ResultValue>
                    <ResultDescription>
                      Faster installation vs traditional methods
                    </ResultDescription>
                  </ResultCard>

                  <ChartContainer>
                    <Doughnut
                      data={savingsChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            labels: {
                              color: '#ffffff'
                            }
                          }
                        }
                      }}
                    />
                  </ChartContainer>
                </motion.div>
              )}
            </AnimatePresence>
          </ResultsSection>
        </CalculatorGrid>

        {showResults && (
          <ComparisonGrid>
            <ComparisonCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 style={{ color: '#ef4444', marginBottom: '1rem' }}>Traditional Method</h4>
              <div style={{ fontSize: '2rem', color: '#ef4444', marginBottom: '0.5rem' }}>
                ₹{(comparisonData.traditional.cost).toLocaleString()}
              </div>
              <p style={{ color: '#94a3b8' }}>Total project cost with lap splicing</p>
            </ComparisonCard>

            <ComparisonCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h4 style={{ color: '#10b981', marginBottom: '1rem' }}>Coupler Solution</h4>
              <div style={{ fontSize: '2rem', color: '#10b981', marginBottom: '0.5rem' }}>
                ₹{results.couplerCost.toLocaleString()}
              </div>
              <p style={{ color: '#94a3b8' }}>Investment cost with {results.roi}% ROI</p>
            </ComparisonCard>

            <ComparisonCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h4 style={{ color: '#3b82f6', marginBottom: '1rem' }}>Net Benefit</h4>
              <div style={{ fontSize: '2rem', color: '#3b82f6', marginBottom: '0.5rem' }}>
                ₹{(results.totalSavings - results.couplerCost).toLocaleString()}
              </div>
              <p style={{ color: '#94a3b8' }}>Direct financial benefit to your project</p>
            </ComparisonCard>
          </ComparisonGrid>
        )}
      </CalculatorContent>
    </CalculatorContainer>
  );
}

export default InteractiveCalculator;